import React, { useEffect } from 'react';
import { Table, Tooltip, Spin, Button } from 'antd';
import { ToolTipInfo } from 'utils/svgs';
import styles from './index.module.scss';

interface SummaryGroup {
	accountId: string;
	accountName: string;
	totalAmount: number;
	customerId: string;
	customerName: string;
	classId: string;
	className: string;
	locationId: string;
	locationName: string;
	vendorId: string;
	vendorName: string;
}

interface Allocation {
	configAccount: string;
	percentage: number;
	allocatedAmount: number;
	customerId: string;
	customerName: string;
	classId: string;
	className: string;
	locationId: string;
	locationName: string;
	vendorId: string;
	vendorName: string;
}

interface ExpenseAllocationData {
	summaryGroup: SummaryGroup;
	allocations: Allocation[];
}

interface Props {
	data: ExpenseAllocationData[];
	hasMore?: boolean;
	isLoadingMore?: boolean;
	onLoadMore?: () => void;
	loading?: boolean;
	selectedExpenseGroup?: string;
	onConfigureAllocations?: () => void;
	hasConfiguredAllocations?: boolean;
	isTemporaryConfigWithNoData?: boolean;
	grandTotal?: number;
	oneOffConfigured?: boolean; // For One-Off mode: true if configured, false if not configured
}

const AllocationTable: React.FC<Props> = ({
	data,
	hasMore = false,
	isLoadingMore = false,
	onLoadMore,
	loading = false,
	selectedExpenseGroup,
	onConfigureAllocations,
	hasConfiguredAllocations = false,
	isTemporaryConfigWithNoData = false,
	grandTotal: propGrandTotal = 0,
	oneOffConfigured,
}) => {
	const isOneOffMode = selectedExpenseGroup === 'all';
	// Infinite scroll logic
	useEffect(() => {
		const handleScroll = (event: any) => {
			if (!hasMore || isLoadingMore) return;

			const { scrollTop, scrollHeight, clientHeight } = event.target;
			const threshold = 10; // Load more when 10px from bottom

			if (scrollTop + clientHeight >= scrollHeight - threshold) {
				onLoadMore?.();
			}
		};

		// Attach scroll listener to the actual table body element
		const tableBody = document.querySelector('.ant-table-body');
		if (tableBody) {
			tableBody.addEventListener('scroll', handleScroll);
			return () => {
				tableBody.removeEventListener('scroll', handleScroll);
			};
		}
	}, [hasMore, isLoadingMore, onLoadMore]);

	// Transform the data structure to flat rows for the table
	const tableData = React.useMemo(() => {
		const rows: any[] = [];
		let globalIndex = 0;

		data.forEach((group) => {
			group.allocations.forEach((allocation, index) => {
				rows.push({
					key: `${group.summaryGroup.accountId}-${group.summaryGroup.customerId}-${group.summaryGroup.classId}-${group.summaryGroup.locationId}-${group.summaryGroup.vendorId}-${index}`,
					account: group.summaryGroup.accountName,
					accountId: group.summaryGroup.accountId,
					customer: allocation.customerName,
					vendor: group.summaryGroup.vendorName,
					class: allocation.className,
					location: allocation.locationName,
					allocation: allocation.percentage,
					amount: allocation.allocatedAmount,
					totalAmount: group.summaryGroup.totalAmount,
					isFirstRow: index === 0,
					isLastRow: index === group.allocations.length - 1,
					rowSpan: group.allocations.length,
					summaryGroup: group.summaryGroup, // Store the full summary group data
					globalIndex: globalIndex++,
				});
			});
		});

		return rows;
	}, [data]);

	// Group key must include vendorId so different vendors
	// get separate merged cells in the Account column instead of one cell showing the first vendor only.
	const summaryGroupKey = (row: { accountId?: string; summaryGroup?: SummaryGroup }) =>
		`${row?.accountId ?? ''}-${row?.summaryGroup?.customerId ?? ''}-${row?.summaryGroup?.classId ?? ''}-${row?.summaryGroup?.locationId ?? ''}-${row?.summaryGroup?.vendorId ?? ''}`;

	const accountRowSpan = React.useMemo(() => {
		const spans: number[] = [];
		let i = 0;

		while (i < tableData.length) {
			const currentRow = tableData[i];
			const currentGroupKey = summaryGroupKey(currentRow);
			let count = 1;
			let j = i + 1;

			while (j < tableData.length) {
				const nextRow = tableData[j];
				const nextGroupKey = summaryGroupKey(nextRow);

				if (nextGroupKey === currentGroupKey) {
					count += 1;
					j += 1;
				} else {
					break;
				}
			}

			// first row of the group gets the span
			spans[i] = count;

			// all other rows in the group get 0
			for (let k = i + 1; k < j; k++) {
				spans[k] = 0;
			}

			i = j;
		}
		return spans;
	}, [tableData]);

	// Vendor column: merge cells by summary group + vendorId (same vendor name for consecutive rows in group)
	const vendorRowSpan = React.useMemo(() => {
		const spans: number[] = [];
		let i = 0;

		while (i < tableData.length) {
			const currentRow = tableData[i];
			const currentGroupKey = summaryGroupKey(currentRow);
			let count = 1;
			let j = i + 1;

			while (j < tableData.length) {
				const nextRow = tableData[j];
				const nextGroupKey = summaryGroupKey(nextRow);

				if (nextGroupKey === currentGroupKey) {
					count += 1;
					j += 1;
				} else {
					break;
				}
			}

			spans[i] = count;
			for (let k = i + 1; k < j; k++) {
				spans[k] = 0;
			}
			i = j;
		}
		return spans;
	}, [tableData]);

	const columns = [
		{
			title: 'Account',
			dataIndex: 'account',
			key: 'account',
			width: 250,
			className: 'account-column',
			cellColor: 'cell-fff',
			onCell: (_: any, index: number) => ({
				rowSpan: accountRowSpan[index] ?? 1,
				className: `account-column cell-fff`,
			}),
			render: (text: string, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<span>{text}</span>
					{record.isFirstRow && (
						<Tooltip
							title={
								<div style={{ fontSize: '12px' }}>
									<div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
										Summary Group
									</div>
									<div>
										Account:{' '}
										{record.summaryGroup?.accountName || record.account}
									</div>
									{record.summaryGroup?.customerName && (
										<div>Customer: {record.summaryGroup.customerName}</div>
									)}
									{record.summaryGroup?.className && (
										<div>Class: {record.summaryGroup.className}</div>
									)}
									{record.summaryGroup?.locationName && (
										<div>Location: {record.summaryGroup.locationName}</div>
									)}
									{record.summaryGroup?.vendorName && (
										<div>Vendor: {record.summaryGroup.vendorName}</div>
									)}
									<div>
										Total Amount: $
										{record.totalAmount.toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</div>
								</div>
							}
						>
							<div style={{ cursor: 'pointer', transform: 'scale(0.8)' }}>
								<ToolTipInfo />
							</div>
						</Tooltip>
					)}
				</div>
			),
		},
		{
			title: 'Vendor',
			dataIndex: 'vendor',
			key: 'vendor',
			width: 250,
			className: 'vendor-column',
			onCell: (_: any, index: number) => ({
				rowSpan: vendorRowSpan[index] ?? 1,
				className: 'vendor-column cell-FCF9E1',
			}),
			render: (text: string) => {
				return (
				<div style={{ display: 'flex', alignItems: 'center', paddingTop: '8px' }}>
					<span>{text || '-'}</span>
				</div>
				)
			}
		},
		{
			title: 'Customer',
			dataIndex: 'customer',
			key: 'customer',
			width: 250,
			className: 'cell-e7eff8',
			onCell: () => ({
				className: 'cell-e7eff8',
			}),
			render: (text: string) => text || '-',
		},
		{
			title: 'Class',
			dataIndex: 'class',
			key: 'class',
			width: 250,
			cellColor: 'cell-e1f1eb',
			onCell: () => ({
				className: 'cell-e1f1eb',
			}),
			render: (text: string) => text || '-',
		},
		{
			title: 'Location',
			dataIndex: 'location',
			key: 'location',
			width: 250,
			cellColor: 'cell-f3ede7',
			onCell: () => ({
				className: 'cell-f3ede7',
			}),
			render: (text: string) => text || '-',
		},
		{
			title: 'Allocation %',
			dataIndex: 'allocation',
			key: 'allocation',
			width: 150,
			align: 'right' as const,
			cellColor: 'cell-dfe9ed',
			onCell: () => ({
				className: 'cell-DFE9ED',
			}),
			render: (v: number) => <p>{`${v.toFixed(2)}%`}</p>,
		},
		{
			title: 'Allocated Amount',
			dataIndex: 'amount',
			key: 'amount',
			width: 200,
			align: 'right' as const,
			cellColor: 'cell-FCF9E1',
			onCell: () => ({
				className: 'cell-FCF9E1',
			}),
			render: (v: number) =>
				`$${v.toLocaleString(undefined, {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})}`,
		},
	];

	// Use grandTotal from props, which should be provided from Redux state
	const grandTotal = propGrandTotal || 0;

	return (
		<div className={styles['expense-allocation-table']}>
			{loading ? (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '60px 20px',
						textAlign: 'center',
						minHeight: '300px',
					}}
				>
					<Spin />
					<div
						style={{
							fontSize: '14px',
							color: '#8c8c8c',
							marginTop: '16px',
						}}
					>
						Loading allocation data...
					</div>
				</div>
			) : data.length === 0 ? (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '60px 20px',
						textAlign: 'center',
						color: '#8c8c8c',
						fontSize: '14px',
						minHeight: '300px',
					}}
				>
					<div
						style={{
							fontSize: '16px',
							fontWeight: '500',
							marginBottom: '8px',
							color: '#595959',
						}}
					>
						{isOneOffMode
							? oneOffConfigured === false || oneOffConfigured === undefined
								? 'No Allocation Configurations Found'
								: 'No Expense Allocation Data Available'
							: hasConfiguredAllocations || isTemporaryConfigWithNoData
								? 'No Expense Allocation Data Available'
								: 'No Allocation Configurations Found'}
					</div>
					<div
						style={{
							fontSize: '12px',
							fontStyle: 'italic',
							maxWidth: '400px',
							lineHeight: '1.5',
							marginBottom: '0',
						}}
					>
						{isOneOffMode
							? oneOffConfigured === false || oneOffConfigured === undefined
								? 'You haven\'t configured any expense allocation data yet. Click "Configure Allocations" below to set up your allocation rules.'
								: 'No expense allocation data is available for the selected date range and filters.'
							: hasConfiguredAllocations || isTemporaryConfigWithNoData
								? 'No expense allocation data is available for the selected date range and filters.'
								: 'You haven\'t configured any expense allocation data yet. Click "Configure Allocations" below to set up your allocation rules.'}
					</div>
					{/* Show Configure Allocations button for One-Off mode when not configured */}
					{isOneOffMode &&
						data.length === 0 &&
						(oneOffConfigured === false || oneOffConfigured === undefined) &&
						onConfigureAllocations && (
							<Button
								onClick={onConfigureAllocations}
								style={{
									border: 'none',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									background: '#344735',
									fontSize: '12px',
									fontWeight: '600',
									borderRadius: '12px',
									cursor: 'pointer',
									minHeight: '41px',
									width: '180px',
									color: 'white',
									marginTop: '16px',
								}}
							>
								Configure Allocations
							</Button>
						)}
					{/* Show Configure Allocations button for normal mode */}
					{!isOneOffMode &&
						selectedExpenseGroup !== 'all' &&
						data.length === 0 &&
						!isTemporaryConfigWithNoData &&
						!hasConfiguredAllocations &&
						onConfigureAllocations && (
							<Button
								onClick={onConfigureAllocations}
								style={{
									border: 'none',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									background: '#344735',
									fontSize: '12px',
									fontWeight: '600',
									borderRadius: '12px',
									cursor: 'pointer',
									minHeight: '41px',
									width: '180px',
									color: 'white',
									marginTop: '16px',
								}}
							>
								Configure Allocations
							</Button>
						)}
				</div>
			) : (
				<>
					<Table
						columns={columns as any}
						dataSource={tableData}
						rowKey="key"
						pagination={false}
						scroll={{ x: 1200, y: 'calc(87vh - 360px)' }}
						rowClassName={(record) => {
							return record.isLastRow ? 'summary-group-end' : '';
						}}
						summary={() => (
							<Table.Summary fixed>
								<Table.Summary.Row>
									<Table.Summary.Cell index={0} colSpan={6}>
										Grand Total:
									</Table.Summary.Cell>
									<Table.Summary.Cell index={6} align="right">
										{`$${grandTotal.toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}`}
									</Table.Summary.Cell>
								</Table.Summary.Row>
							</Table.Summary>
						)}
					/>
				</>
			)}
			{isLoadingMore && (
				<div
					style={{
						textAlign: 'right',
						padding: '0 20px',
						fontSize: '12px',
						color: '#344735',
						marginTop: '8px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
					}}
				>
					<Spin size="small" />
					<span style={{ marginLeft: '8px' }}>Loading more allocations...</span>
				</div>
			)}
			{!hasMore && data.length > 0 && (
				<div
					style={{
						textAlign: 'right',
						padding: '0 20px',
						color: '#344735',
						fontSize: '12px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						marginTop: '8px',
					}}
				>
					No more allocations
				</div>
			)}
		</div>
	);
};

export default AllocationTable;
